import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import type { DiscussionTopicRow, InvestmentProductRow } from '../../store/insights/insights'
import { fmt, type SortDir } from './insightsUtils'
import { InsightsSection, InfoTooltip, TrendBadge, SortIcon, EmptyState } from './insightsCommon'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

const DEFAULT_VISIBLE_ROWS = 5

type TopicSortKey = 'topic' | 'mentions' | 'clientDrivenPercent' | 'trendValue' | 'questionsRaised'

const SortableHeader: React.FC<{
  label: string
  sortKey: TopicSortKey
  activeKey: TopicSortKey
  activeDir: SortDir
  onSort: (key: TopicSortKey) => void
  tooltip?: string
}> = ({ label, sortKey, activeKey, activeDir, onSort, tooltip }) => {
  const isActive = sortKey === activeKey
  const ariaSort: 'none' | 'ascending' | 'descending' = isActive ? (activeDir === 'asc' ? 'ascending' : 'descending') : 'none'
  return (
    <th className='insights-sortable-th' aria-sort={ariaSort} onClick={() => onSort(sortKey)}>
      <div className='insights-sortable-th-inner'>
        <span className='insights-label-with-tooltip'>
          {label}
          {tooltip && <InfoTooltip text={tooltip} />}
        </span>
        {isActive && <SortIcon dir={activeDir} />}
      </div>
    </th>
  )
}

function sortTopicRows<T extends { topic: string; mentions: number; clientDrivenPercent: number; trendValue: number | null; questionsRaised: number }>(
  rows: T[],
  key: TopicSortKey,
  dir: SortDir,
  nameField: keyof T = 'topic' as keyof T,
): T[] {
  return [...rows].sort((a, b) => {
    let cmp: number
    if (key === 'topic') {
      cmp = String(a[nameField]).localeCompare(String(b[nameField]))
    } else {
      const av = (a[key as keyof T] as number) ?? 0
      const bv = (b[key as keyof T] as number) ?? 0
      cmp = av - bv
    }
    return dir === 'asc' ? cmp : -cmp
  })
}

function useTopicSort(defaultKey: TopicSortKey = 'mentions', defaultDir: SortDir = 'desc') {
  const [sortKey, setSortKey] = useState<TopicSortKey>(defaultKey)
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir)

  const onSort = (key: TopicSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'topic' ? 'asc' : 'desc')
    }
  }

  return { sortKey, sortDir, onSort }
}

const SORTABLE_COLUMNS: Array<{ label: string; sortKey: TopicSortKey; tooltip: string }> = [
  { label: 'MENTIONS', sortKey: 'mentions', tooltip: 'Total number of times this topic came up across all conversations' },
  { label: 'CLIENT-DRIVEN', sortKey: 'clientDrivenPercent', tooltip: 'Topics the client raised themselves, without prompting from the advisor' },
  { label: 'TREND', sortKey: 'trendValue', tooltip: 'Change in mentions compared to the previous period' },
  { label: 'QUESTIONS RAISED', sortKey: 'questionsRaised', tooltip: 'Total number of questions clients asked about this topic across all conversations' },
]

const DiscussionTopicsTable: React.FC<{ rows: DiscussionTopicRow[]; total: number }> = ({ rows, total }) => {
  const [expanded, setExpanded] = useState(false)
  const { sortKey, sortDir, onSort: onSortBase } = useTopicSort('mentions', 'desc')
  const { trackingClient } = useTracking()
  const onSort = (key: TopicSortKey) => {
    onSortBase(key)
    trackingClient.track({ eventName: TrackEvent.INSIGHTS_SORT_CHANGED, eventDetails: { section: 'discussionTopics', column: key } })
  }
  const sortedRows = useMemo(() => sortTopicRows(rows, sortKey, sortDir), [rows, sortKey, sortDir])
  const visibleRows = expanded ? sortedRows : sortedRows.slice(0, DEFAULT_VISIBLE_ROWS)
  const hasMore = total > DEFAULT_VISIBLE_ROWS

  return (
    <>
      <table className='table table-borderless mb-0 insights-breakdown-table'>
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <SortableHeader label='TOPIC' sortKey='topic' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
            {SORTABLE_COLUMNS.map((col) => (
              <SortableHeader
                key={col.sortKey}
                label={col.label}
                sortKey={col.sortKey}
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={onSort}
                tooltip={col.tooltip}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <tr key={row.topic}>
              <td className='fw-semibold'>{row.topic}</td>
              <td>{fmt(row.mentions)}</td>
              <td>
                <div className='d-flex align-items-center gap-2'>
                  <span>{row.clientDrivenPercent}%</span>
                  <div className='insights-mini-progress'>
                    <div className='insights-mini-progress__fill--purple' style={{ width: `${row.clientDrivenPercent}%` }} />
                  </div>
                </div>
              </td>
              <td>
                <TrendBadge value={row.trendValue} direction={row.trendDirection} />
              </td>
              <td>{fmt(row.questionsRaised)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className='text-center mt-3'>
          <button type='button' className='btn btn-link p-0 insights-link border-0 bg-transparent' onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? 'Show less' : `Show all topics \u2193`}
          </button>
        </div>
      )}
    </>
  )
}

const InvestmentProductsTable: React.FC<{ rows: InvestmentProductRow[]; total: number }> = ({ rows, total }) => {
  const [expanded, setExpanded] = useState(false)
  const { sortKey, sortDir, onSort: onSortBase } = useTopicSort('mentions', 'desc')
  const { trackingClient } = useTracking()
  const onSort = (key: TopicSortKey) => {
    onSortBase(key)
    trackingClient.track({ eventName: TrackEvent.INSIGHTS_SORT_CHANGED, eventDetails: { section: 'investmentProducts', column: key } })
  }
  const sortedRows = useMemo(() => sortTopicRows(rows, sortKey, sortDir, 'subTopic'), [rows, sortKey, sortDir])
  const visibleRows = expanded ? sortedRows : sortedRows.slice(0, DEFAULT_VISIBLE_ROWS)
  const hasMore = total > DEFAULT_VISIBLE_ROWS

  return (
    <>
      <table className='table table-borderless mb-0 insights-breakdown-table'>
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <SortableHeader label='ASSET CLASS' sortKey='topic' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
            {SORTABLE_COLUMNS.map((col) => (
              <SortableHeader
                key={col.sortKey}
                label={col.label}
                sortKey={col.sortKey}
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={onSort}
                tooltip={col.tooltip}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, i) => (
            <tr key={`${row.topic}-${row.subTopic}-${i}`}>
              <td className='fw-semibold'>{row.subTopic}</td>
              <td>{fmt(row.mentions)}</td>
              <td>
                <div className='d-flex align-items-center gap-2'>
                  <span>{row.clientDrivenPercent}%</span>
                  <div className='insights-mini-progress'>
                    <div className='insights-mini-progress__fill--purple' style={{ width: `${row.clientDrivenPercent}%` }} />
                  </div>
                </div>
              </td>
              <td>
                <TrendBadge value={row.trendValue} direction={row.trendDirection} />
              </td>
              <td>{fmt(row.questionsRaised)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className='text-center mt-3'>
          <button type='button' className='btn btn-link p-0 insights-link border-0 bg-transparent' onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? 'Show less' : `Show all products \u2193`}
          </button>
        </div>
      )}
    </>
  )
}

export const DiscussionTopicsSection: React.FC<{
  topics: DiscussionTopicRow[]
  products: InvestmentProductRow[]
}> = ({ topics, products }) => {
  const [activeTab, setActiveTab] = useState<'topics' | 'products'>('topics')
  const { trackingClient } = useTracking()
  const hasTopics = topics.length > 0
  const hasProducts = products.length > 0

  const tabSwitcher = (
    <div className='insights-tab-switcher'>
      <button
        type='button'
        className={classNames('insights-tab-switcher__btn', { 'insights-tab-switcher__btn--active': activeTab === 'topics' })}
        onClick={() => {
          setActiveTab('topics')
          trackingClient.track({ eventName: TrackEvent.INSIGHTS_TAB_CHANGED, eventDetails: { tab: 'topics' } })
        }}
      >
        Discussion topics
      </button>
      <button
        type='button'
        className={classNames('insights-tab-switcher__btn', { 'insights-tab-switcher__btn--active': activeTab === 'products' })}
        onClick={() => {
          setActiveTab('products')
          trackingClient.track({ eventName: TrackEvent.INSIGHTS_TAB_CHANGED, eventDetails: { tab: 'products' } })
        }}
      >
        Investment Products
      </button>
    </div>
  )

  return (
    <InsightsSection title='Discussion topics' subtitle="See what topics are coming up and prepare for what's next" headerRight={tabSwitcher}>
      <div className={classNames('insights-tab-panel', { 'insights-tab-panel--hidden': activeTab !== 'topics' })}>
        {hasTopics ? (
          <DiscussionTopicsTable rows={topics} total={topics.length} />
        ) : (
          <EmptyState icon={<i className='bi bi-chat-dots' />} title='No topics found' subtitle='Try selecting a wider time range' />
        )}
      </div>
      <div className={classNames('insights-tab-panel', { 'insights-tab-panel--hidden': activeTab !== 'products' })}>
        {hasProducts ? (
          <InvestmentProductsTable rows={products} total={products.length} />
        ) : (
          <EmptyState icon={<i className='bi bi-chat-dots' />} title='No investment products found' subtitle='Try selecting a wider time range' />
        )}
      </div>
    </InsightsSection>
  )
}
